<template>
  <v-dialog :model-value="modelValue" width="560" @update:model-value="v => $emit('update:modelValue', v)">
    <v-card>
      <v-card-title>{{ personName }}'s Booking History</v-card-title>
      <v-card-text>
        <v-skeleton-loader v-if="peopleStore.historyLoading" type="list-item-three-line@3" />
        <template v-else>
          <p class="text-body2 mb-4">
            {{ upcoming.length }} upcoming · {{ worked.length }} worked · {{ cancelledOrDeclined.length }} cancelled/declined
            <template v-if="feeTotal > 0"> · ${{ feeTotal.toLocaleString() }} in fees</template>
          </p>

          <template v-if="!history.length">
            <p class="text-body2 text-medium-emphasis text-center py-6">No booking history for this person yet.</p>
          </template>

          <template v-else>
            <div v-if="upcoming.length" class="mb-4">
              <p class="text-caption text-medium-emphasis mb-1">Upcoming</p>
              <v-list density="compact">
                <v-list-item v-for="b in upcoming" :key="b.id">
                  <v-list-item-title>{{ b.gigName ?? 'Gig' }} — {{ b.role ?? '' }}</v-list-item-title>
                  <v-list-item-subtitle>{{ dateSummary(b) }} — {{ rateSummary(b) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>

            <div v-if="worked.length" class="mb-4">
              <p class="text-caption text-medium-emphasis mb-1">Worked</p>
              <v-list density="compact">
                <v-list-item v-for="b in worked" :key="b.id">
                  <v-list-item-title>{{ b.gigName ?? 'Gig' }} — {{ b.role ?? '' }}</v-list-item-title>
                  <v-list-item-subtitle>{{ dateSummary(b) }} — {{ rateSummary(b) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>

            <div v-if="cancelledOrDeclined.length">
              <p class="text-caption text-medium-emphasis mb-1">Cancelled &amp; Declined</p>
              <v-list density="compact">
                <v-list-item v-for="b in cancelledOrDeclined" :key="b.id">
                  <v-list-item-title>{{ b.gigName ?? 'Gig' }} — {{ b.role ?? '' }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ b.state === 'CANCELLED' ? 'Cancelled' : 'Declined' }}
                    <template v-if="b.cancellationFee">
                      — Fee ${{ b.cancellationFee.amount.toLocaleString() }} ({{ b.cancellationFeeStatus?.toLowerCase() }})
                    </template>
                    <template v-if="b.cancellationReason"> — {{ b.cancellationReason }}</template>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>
          </template>
        </template>

        <div class="text-right mt-4">
          <v-btn text @click="$emit('update:modelValue', false)">Close</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import type { Booking } from '@gigdriver/domain';
import { isPastBooking } from '@gigdriver/domain';
import { usePeopleStore } from '../stores/people';
import { useOrganizationStore } from '../stores/organization';

const props = defineProps<{
  modelValue: boolean;
  orgId: string;
  personId: string;
  personName: string;
}>();
defineEmits<{ 'update:modelValue': [boolean] }>();

const peopleStore = usePeopleStore();
const orgStore = useOrganizationStore();

const history = computed<Booking[]>(() => peopleStore.personHistory.get(props.personId) ?? []);

const timeZone = computed(() => orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);

const upcoming = computed(() =>
  history.value.filter(b => (b.state === 'ACCEPTED' || b.state === 'BOOKED') && !isPastBooking(b, new Date(), b.venueTimeZone ?? timeZone.value)),
);
const worked = computed(() =>
  history.value.filter(b => (b.state === 'ACCEPTED' || b.state === 'BOOKED' || b.state === 'COMPLETED') && isPastBooking(b, new Date(), b.venueTimeZone ?? timeZone.value)),
);
const cancelledOrDeclined = computed(() => history.value.filter(b => b.state === 'CANCELLED' || b.state === 'DECLINED'));

const feeTotal = computed(() =>
  history.value.reduce((sum, b) => sum + (b.cancellationFee && b.cancellationFeeStatus !== 'WAIVED' ? b.cancellationFee.amount : 0), 0),
);

function dateSummary(b: Booking): string {
  const calls = b.linkedCalls;
  if (calls?.length) {
    const start = new Date(Math.min(...calls.map(c => new Date(c.startTime).getTime())));
    return start.toLocaleDateString();
  }
  if (b.scopeDate) return new Date(b.scopeDate).toLocaleDateString();
  return 'Unscheduled';
}

function rateSummary(b: Booking): string {
  if (!b.rate) return '—';
  const suffix = b.rateType === 'hourly' ? '/hr' : b.rateType === 'day' ? '/day' : '';
  return `$${b.rate.amount}${suffix}`;
}

watch(
  () => [props.modelValue, props.personId] as const,
  ([open, personId]) => {
    if (open && personId) peopleStore.loadPersonHistory(props.orgId, personId);
  },
  { immediate: true },
);
</script>
